import { useEffect, useState } from "react";
import { Hand, Radio } from "lucide-react";
import { LiveZoya } from "@/components/LiveZoya";
import { LangPicker } from "@/components/smart/ClassControls";
import { useLang } from "@/context/LanguageContext";
import { clip } from "@/lib/zoya";
import { cleanName, markPresent } from "@/lib/classroom_live";

// Start-of-class overlay: Zoya Ma'am greets the student by name and takes attendance ("Present Ma'am").
export const ClassStart = ({ lesson, me, audio, onStart }) => {
  const { L, lang } = useLang();
  const [val, setVal] = useState(me.name);
  const [step, setStep] = useState("name");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Signed-in students: /student/me resolves after mount, so fill the name in when it arrives.
  useEffect(() => { if (me.name) setVal((v) => v || me.name); }, [me.name]);
  const first = (cleanName(val) || me.first).split(" ")[0];

  const join = (e) => {
    e.preventDefault();
    const n = me.save(val).split(" ")[0];
    if (!n) return;
    setStep("greet");
    const say = L(`Namaste ${n}! Welcome to today's RoboSpark class. Today we will learn ${lesson.title}. Please tap Present Ma'am for your attendance.`,
      `नमस्ते ${n}! आज की RoboSpark class में आपका स्वागत है. आज हम सीखेंगे ${lesson.title_hi}. अपनी attendance के लिए Present Ma'am दबाइए.`);
    audio.say(say, lang, clip("greet"));
  };
  const present = async () => {
    setBusy(true); setErr("");
    try {
      const r = await markPresent(lesson.slug);
      audio.stop();
      onStart(r);
    } catch {
      setErr(L("Couldn't save your attendance. Please try again.", "Attendance सेव नहीं हो पाई। फिर से दबाइए।"));
    } finally { setBusy(false); }
  };

  return (
    <div className="relative mt-3 sm:mt-0 sm:absolute sm:inset-0 z-10 sm:rounded-3xl sm:bg-[#E8F0FE]/75 sm:backdrop-blur-md grid place-items-center sm:p-6 sm:overflow-y-auto" data-testid="class-start">
      <div className="w-full max-w-2xl flex items-start sm:items-center gap-3 sm:gap-6 rounded-3xl bg-white/90 border border-white shadow-[0_30px_80px_-30px_rgba(10,25,47,0.5)] p-3 sm:p-6 animate-in fade-in zoom-in-95">
        <LiveZoya src={lesson.teacher} speaking={audio.speaking} levelRef={audio.levelRef} greeting={step === "greet"} className="w-20 sm:w-52 shrink-0 rounded-2xl" testid="class-start-zoya" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#007A8A]"><Radio className="w-3.5 h-3.5 text-[#FF2E63]" />{L(`Lesson ${lesson.n} · class is starting`, `पाठ ${lesson.n} · क्लास शुरू हो रही है`)}</div>
          {step === "name" ? (
            <form onSubmit={join}>
              <h3 className="mt-1 text-base sm:text-2xl font-extrabold">{L("What's your name?", "आपका नाम क्या है?")}</h3>
              <p className="hidden sm:block text-sm text-slate-500">{L("Zoya Ma'am will greet you by name and take attendance.", "ज़ोया मैम आपका नाम लेकर स्वागत करेंगी और attendance लेंगी।")}</p>
              <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row gap-2">
                <input value={val} onChange={(e) => setVal(e.target.value)} maxLength={40} placeholder={L("Your name", "आपका नाम")} data-testid="class-name-input"
                  className="w-full sm:w-auto sm:flex-1 min-w-0 h-11 sm:h-12 px-4 rounded-full bg-[#F0F4F8] border border-slate-300 focus:border-[#00B8D4] outline-none text-sm" />
                <button type="submit" disabled={!cleanName(val)} data-testid="class-join-btn"
                  className="h-11 sm:h-12 px-4 sm:px-6 rounded-full bg-[#FF3B3B] hover:bg-[#E63946] text-white font-bold text-sm whitespace-nowrap disabled:opacity-40 transition-colors">{L("Join class", "क्लास जॉइन करें")}</button>
              </div>
              <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <LangPicker compact testid="class-start-lang" />
                <span className="text-[11px] text-slate-500">{L("Zoya's answers, voice and labs use this language.", "ज़ोया मैम के जवाब, आवाज़ और लैब इसी भाषा में।")}</span>
              </div>
            </form>
          ) : (
            <>
              <p className="mt-1 text-sm sm:text-lg font-bold leading-snug" data-testid="class-greeting-text">
                {L(`Namaste ${first}! Welcome to today's class.`, `नमस्ते ${first}! आज की क्लास में आपका स्वागत है।`)}
                <span className="hidden sm:inline font-medium text-slate-600"> {L(`Today: ${lesson.title}.`, `आज: ${lesson.title_hi}।`)}</span>
              </p>
              <button onClick={present} disabled={busy} data-testid="class-present-btn"
                className="mt-2 sm:mt-4 inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-sm sm:text-base shadow-[0_12px_30px_-10px_rgba(5,150,105,0.7)] active:scale-95 transition-[background-color,transform] disabled:opacity-60">
                <Hand className="w-5 h-5" />{L("Present Ma'am!", "Present Ma'am!")}
              </button>
              {err && <p className="mt-1 text-xs text-[#C81E4B]" data-testid="class-present-error">{err}</p>}
            </>
          )}
          <button type="button" onClick={() => { audio.stop(); onStart(null); }} data-testid="class-skip-btn"
            className="mt-1 block py-2 text-xs text-slate-500 underline-offset-4 hover:underline hover:text-[#0A192F]">{L("Just watch, without attendance", "बिना attendance सिर्फ़ देखें")}</button>
        </div>
      </div>
    </div>
  );
};
